import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, ExternalLink, Facebook, Instagram } from "lucide-react";

const Footer = () => {
  const contactInfo = [
    {
      icon: MapPin,
      label: "Morada",
      value: "Av. Almirante Afonso Cerqueira nº. 67",
      subvalue: "(Ed. Tojal e Tojal) — 3510-023 Viseu",
      link: "https://maps.google.com/?q=Avenida+Almirante+Afonso+Cerqueira+67+Viseu",
    },
    {
      icon: Phone,
      label: "Telefone",
      value: "232 088 790",
      link: "tel:232088790",
    },
    {
      icon: Phone,
      label: "Urgências",
      value: "927 525 885",
      link: "tel:927525885",
    },
    {
      icon: Mail,
      label: "Email",
      value: "cligeral2care@gmail.com",
      link: "mailto:cligeral2care@gmail.com",
    },
    {
      icon: Clock,
      label: "Horário",
      value: "Segunda a Sexta: 10h - 19h",
    },
  ];

  const legalLinks = [
    {
      name: "Livro de Reclamações",
      url: "https://www.livroreclamacoes.pt/",
    },
    {
      name: "Política de Privacidade",
      url: "#privacidade",
    },
  ];

  return (
    <footer
      id="contactos"
      className="bg-[#1A1A1A] text-white py-16"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Left Column - Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Logo */}
            <div className="flex items-center gap-2 mb-8">
              <img 
                src="/logo.png" 
                alt="Cligeral II Care Logo"
                className="w-12 h-12 object-contain bg-white rounded-lg p-1"
              />
              <div>
                <span className="font-outfit font-bold text-xl text-white">
                  Cligeral II
                </span>
                <span className="font-outfit font-medium text-xl text-gray-400"> Care</span>
              </div>
            </div>

            <p className="text-white/70 mb-8 max-w-md">
              Centro Avançado de Reabilitação Estética. Medicina dentária de
              excelência em Viseu, com uma equipa especializada dedicada ao seu
              sorriso.
            </p>

            {/* Contact Items */}
            <div className="space-y-4">
              {contactInfo.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3"
                  data-testid={`footer-contact-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-xs font-semibold tracking-wider uppercase text-white/50 block">
                      {item.label}
                    </span>
                    {item.link ? (
                      <a
                        href={item.link}
                        target={item.link.startsWith("http") ? "_blank" : undefined}
                        rel={item.link.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="text-white hover:text-gray-300 transition-colors"
                        data-testid={`footer-link-${item.label.toLowerCase()}`}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="text-white">{item.value}</span>
                    )}
                    {item.subvalue && (
                      <span className="text-white/60 block text-sm">
                        {item.subvalue}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4 mt-8">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-colors"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/cligeralcare/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-colors"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </motion.div>

          {/* Right Column - Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-outfit text-xl font-semibold mb-4">
              A Nossa Localização
            </h3>
            <div className="map-container aspect-video md:aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
              <iframe
                src="https://maps.google.com/maps?q=Avenida%20Almirante%20Afonso%20Cerqueira,%20n%C2%BA%2063%20e%2067,%203510-023%20Viseu&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Cligeral II Care"
                data-testid="google-map"
              />
            </div>
            <a
              href="https://maps.google.com/?q=Avenida+Almirante+Afonso+Cerqueira+67+Viseu"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mt-4 text-sm"
              data-testid="open-maps-link"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir no Google Maps
            </a>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Cligeral II Care, Lda. Todos os
              direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              {legalLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target={link.url.startsWith("http") ? "_blank" : undefined}
                  rel={link.url.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="text-white/50 hover:text-white text-sm transition-colors"
                  data-testid={`legal-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
